import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Progress } from '../components/ui/progress';
import {
  Activity,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Star,
  Send,
  Circle,
  Check,
  Upload,
  CreditCard,
  Building2,
  FileText,
  X
} from 'lucide-react';
import { authAPI, surveysAPI, filesAPI } from '../services/api';

const SurveyResponsePage = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('consent'); // consent, documents, survey, thankyou
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Document collection state
  const [documentData, setDocumentData] = useState({
    panCard: null,
    cancelledCheque: null,
    accountHolderName: '',
    bankAccountNo: '',
    ifscCode: '',
    bankNameBranch: ''
  });
  const [documentErrors, setDocumentErrors] = useState({});
  const [uploadingFile, setUploadingFile] = useState(null);

  useEffect(() => {
    loadData();
  }, [surveyId]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      // Get survey data
      const surveyData = await surveysAPI.getById(surveyId);
      setSurvey(surveyData);
      
      // If no consent form, skip to survey
      if (!surveyData.consent_form || !surveyData.consent_form.content) {
        setCurrentStep('survey');
      }
    } catch (error) {
      console.error('Error loading survey:', error);
      setError('Survey not found or you do not have access');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConsentAccept = () => {
    setConsentAccepted(true);
    // Check if document collection is enabled
    if (survey?.consent_form?.collectDocuments) {
      setCurrentStep('documents');
    } else {
      setCurrentStep('survey');
    }
  };

  const handleFileChange = async (field, file) => {
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setDocumentErrors(prev => ({ ...prev, [field]: 'File size must be less than 5MB' }));
        return;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setDocumentErrors(prev => ({ ...prev, [field]: 'Only JPG, PNG, or PDF files are allowed' }));
        return;
      }
      
      // Upload file to server
      setUploadingFile(field);
      try {
        const docType = field === 'panCard' ? 'pan_card' : 'cancelled_cheque';
        const result = await filesAPI.upload(docType, file);
        setDocumentErrors(prev => ({ ...prev, [field]: null }));
        setDocumentData(prev => ({ 
          ...prev, 
          [field]: {
            file: file,
            filename: file.name,
            url: result.url,
            saved_filename: result.saved_filename
          }
        }));
      } catch (error) {
        setDocumentErrors(prev => ({ ...prev, [field]: 'Failed to upload file. Please try again.' }));
      } finally {
        setUploadingFile(null);
      }
    }
  };

  const validateDocuments = () => {
    const errors = {};
    const fields = survey?.consent_form?.documentFields || {};
    
    if (fields.panCard && !documentData.panCard) {
      errors.panCard = 'Pan Card is required';
    }
    if (fields.cancelledCheque && !documentData.cancelledCheque) {
      errors.cancelledCheque = 'Cancelled Cheque is required';
    }
    if (fields.accountHolderName && !documentData.accountHolderName.trim()) {
      errors.accountHolderName = 'Account Holder Name is required';
    }
    if (fields.bankAccountNo) {
      if (!documentData.bankAccountNo.trim()) {
        errors.bankAccountNo = 'Bank Account No. is required';
      } else if (!/^\d+$/.test(documentData.bankAccountNo)) {
        errors.bankAccountNo = 'Bank Account No. must contain only numbers';
      }
    }
    if (fields.ifscCode) {
      if (!documentData.ifscCode.trim()) {
        errors.ifscCode = 'IFSC Code is required';
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(documentData.ifscCode.toUpperCase())) {
        errors.ifscCode = 'Invalid IFSC Code format';
      }
    }
    if (fields.bankNameBranch && !documentData.bankNameBranch.trim()) {
      errors.bankNameBranch = 'Bank Name & Branch is required';
    }
    
    setDocumentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDocumentSubmit = () => {
    if (validateDocuments()) {
      setCurrentStep('survey');
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultipleChoiceChange = (questionId, option, checked) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...currentAnswers, option] };
      } else {
        return { ...prev, [questionId]: currentAnswers.filter(a => a !== option) };
      }
    });
  };

  // Check if a question has been answered
  const isQuestionAnswered = (question) => {
    const answer = answers[question.id];
    if (!answer) return false;
    if (Array.isArray(answer) && answer.length === 0) return false;
    if (typeof answer === 'string' && answer.trim() === '') return false;
    return true;
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!survey?.questions || survey.questions.length === 0) return 0;
    const answeredCount = survey.questions.filter(q => isQuestionAnswered(q)).length;
    return Math.round((answeredCount / survey.questions.length) * 100);
  };

  // Check if all mandatory questions are answered
  const areAllMandatoryAnswered = () => {
    if (!survey?.questions) return true;
    const mandatoryQuestions = survey.questions.filter(q => q.required);
    return mandatoryQuestions.every(q => isQuestionAnswered(q));
  };

  const handleSubmit = async () => {
    // Validate required questions
    const requiredQuestions = survey.questions.filter(q => q.required);
    const unansweredRequired = requiredQuestions.filter(q => !isQuestionAnswered(q));
    
    if (unansweredRequired.length > 0) {
      setError(`Please answer all required questions (${unansweredRequired.length} remaining)`);
      // Scroll to first unanswered required question
      const firstUnanswered = document.getElementById(`question-${unansweredRequired[0].id}`);
      if (firstUnanswered) {
        firstUnanswered.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const submissionData = {
        answers: Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer })),
        consent_accepted: consentAccepted || !survey.consent_form
      };
      
      // Include document data if collection was enabled
      if (survey?.consent_form?.collectDocuments) {
        submissionData.banking_details = {
          account_holder_name: documentData.accountHolderName,
          bank_account_no: documentData.bankAccountNo,
          ifsc_code: documentData.ifscCode,
          bank_name_branch: documentData.bankNameBranch
        };
        
        // Include uploaded file references
        submissionData.documents = {};
        if (documentData.panCard?.url) {
          submissionData.documents.pan_card = documentData.panCard.saved_filename || documentData.panCard.filename;
          submissionData.documents.pan_card_url = documentData.panCard.url;
        }
        if (documentData.cancelledCheque?.url) {
          submissionData.documents.cancelled_cheque = documentData.cancelledCheque.saved_filename || documentData.cancelledCheque.filename;
          submissionData.documents.cancelled_cheque_url = documentData.cancelledCheque.url;
        }
      }
      
      await surveysAPI.submitResponse(surveyId, submissionData);
      setCurrentStep('thankyou');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question, index) => {
    const value = answers[question.id];
    const isAnswered = isQuestionAnswered(question);
    
    return (
      <Card 
        key={question.id} 
        id={`question-${question.id}`}
        className={`transition-all duration-300 ${
          isAnswered 
            ? 'bg-emerald-500/5 border-emerald-500/30' 
            : 'bg-slate-800/50 border-slate-700'
        }`}
      >
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            {/* Question Number with Completion Indicator */}
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all text-sm sm:text-base ${
              isAnswered 
                ? 'bg-emerald-500 text-white' 
                : 'bg-slate-700 text-slate-300'
            }`}>
              {isAnswered ? (
                <Check className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <span className="font-semibold">{index + 1}</span>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="mb-3 sm:mb-4">
                <p className={`font-medium text-sm sm:text-base ${isAnswered ? 'text-emerald-300' : 'text-white'}`}>
                  {question.text}
                  {question.required && <span className="text-red-400 ml-1">*</span>}
                </p>
                {question.description && (
                  <p className="text-slate-400 text-xs sm:text-sm mt-1">{question.description}</p>
                )}
              </div>

              {question.type === 'text' && (
                <Input
                  value={value || ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder="Your answer..."
                  className={`${isAnswered ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-700 border-slate-600'} text-white text-sm sm:text-base`}
                />
              )}

              {question.type === 'textarea' && (
                <Textarea
                  value={value || ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder="Your answer..."
                  className={`${isAnswered ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-700 border-slate-600'} text-white min-h-[100px]`}
                />
              )}

              {question.type === 'number' && (
                <Input
                  type="number"
                  value={value || ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder="0"
                  className={`${isAnswered ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-700 border-slate-600'} text-white w-32`}
                />
              )}

              {question.type === 'single_choice' && (
                <RadioGroup value={value || ''} onValueChange={(v) => handleAnswerChange(question.id, v)}>
                  <div className="space-y-2">
                    {question.options?.map((opt, i) => (
                      <div key={i} className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                        value === opt ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-700/50 hover:bg-slate-700'
                      }`}>
                        <RadioGroupItem value={opt} id={`${question.id}-${i}`} className="border-slate-500" />
                        <Label htmlFor={`${question.id}-${i}`} className="text-slate-300 cursor-pointer flex-1">{opt}</Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              )}

              {question.type === 'multiple_choice' && (
                <div className="space-y-2">
                  {question.options?.map((opt, i) => (
                    <div key={i} className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                      (value || []).includes(opt) ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-700/50 hover:bg-slate-700'
                    }`}>
                      <Checkbox
                        id={`${question.id}-${i}`}
                        checked={(value || []).includes(opt)}
                        onCheckedChange={(checked) => handleMultipleChoiceChange(question.id, opt, checked)}
                        className="border-slate-500"
                      />
                      <Label htmlFor={`${question.id}-${i}`} className="text-slate-300 cursor-pointer flex-1">{opt}</Label>
                    </div>
                  ))}
                </div>
              )}

              {question.type === 'yes_no' && (
                <RadioGroup value={value || ''} onValueChange={(v) => handleAnswerChange(question.id, v)}>
                  <div className="flex gap-4">
                    <div className={`flex items-center space-x-2 p-3 px-6 rounded-lg transition-colors ${
                      value === 'yes' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-700/50 hover:bg-slate-700'
                    }`}>
                      <RadioGroupItem value="yes" id={`${question.id}-yes`} className="border-slate-500" />
                      <Label htmlFor={`${question.id}-yes`} className="text-slate-300 cursor-pointer">Yes</Label>
                    </div>
                    <div className={`flex items-center space-x-2 p-3 px-6 rounded-lg transition-colors ${
                      value === 'no' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-700/50 hover:bg-slate-700'
                    }`}>
                      <RadioGroupItem value="no" id={`${question.id}-no`} className="border-slate-500" />
                      <Label htmlFor={`${question.id}-no`} className="text-slate-300 cursor-pointer">No</Label>
                    </div>
                  </div>
                </RadioGroup>
              )}

              {question.type === 'likert' && (
                <div className="grid grid-cols-5 gap-2">
                  {['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'].map((label, i) => (
                    <button
                      key={i}
                      onClick={() => handleAnswerChange(question.id, i + 1)}
                      className={`py-3 px-2 rounded-lg text-center text-sm transition-all ${
                        value === i + 1 
                          ? 'bg-emerald-500 text-white scale-105' 
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {question.type === 'rating' && (
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => handleAnswerChange(question.id, n)}
                      className="p-2 transition-transform hover:scale-110"
                    >
                      <Star className={`w-10 h-10 ${value >= n ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Card className="bg-slate-900 border-slate-800 max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Error</h2>
            <p className="text-slate-400 mb-4">{error}</p>
            <Button onClick={() => navigate('/doctor')} className="bg-slate-800 hover:bg-slate-700">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = calculateProgress();
  const canSubmit = areAllMandatoryAnswered();

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-950/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-30 safe-area-top">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <button 
                onClick={() => navigate('/doctor')}
                className="p-2 -ml-2 text-slate-400 hover:text-white lg:hidden"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-xs sm:text-sm text-slate-400">Logged in as</p>
              <p className="text-white font-medium text-sm sm:text-base">{user?.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-24">
        {/* Consent Form Step */}
        {currentStep === 'consent' && survey?.consent_form && survey.consent_form.content && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="text-center pb-2 px-4 sm:px-6">
              <CardTitle className="text-xl sm:text-2xl text-white">{survey.consent_form.title || 'Consent Form'}</CardTitle>
              <CardDescription className="text-slate-400">{survey.title}</CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-8">
              <div className="prose prose-invert max-w-none mb-6 sm:mb-8">
                <div className="bg-slate-800/50 rounded-xl p-4 sm:p-6 text-slate-300 whitespace-pre-wrap max-h-[300px] sm:max-h-[400px] overflow-y-auto text-sm sm:text-base">
                  {survey.consent_form.content}
                </div>
              </div>
              
              <div className="flex items-start gap-3 mb-6 p-3 sm:p-4 bg-slate-800 rounded-lg">
                <Checkbox
                  id="consent"
                  checked={consentAccepted}
                  onCheckedChange={setConsentAccepted}
                  className="mt-1 border-2 border-white data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                />
                <Label htmlFor="consent" className="text-slate-300 cursor-pointer text-sm sm:text-base">
                  I have read and understood the above consent form and agree to participate in this survey.
                </Label>
              </div>

              <Button
                onClick={handleConsentAccept}
                disabled={!consentAccepted}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 h-12"
              >
                Continue to Survey <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Document Collection Step */}
        {currentStep === 'documents' && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="text-center pb-2 px-4 sm:px-6">
              <CardTitle className="text-xl sm:text-2xl text-white flex items-center justify-center gap-2">
                <Upload className="w-6 h-6 text-amber-400" />
                Document & Banking Details
              </CardTitle>
              <CardDescription className="text-slate-400">Please provide the following details for honorarium payment</CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-8 space-y-6">
              {/* Document Uploads */}
              {(survey?.consent_form?.documentFields?.panCard || survey?.consent_form?.documentFields?.cancelledCheque) && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-cyan-400" /> Document Uploads
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {survey?.consent_form?.documentFields?.panCard && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Pan Card *</Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                            onChange={(e) => handleFileChange('panCard', e.target.files[0])}
                            className="hidden"
                            id="panCard"
                            disabled={uploadingFile === 'panCard'}
                          />
                          <label
                            htmlFor="panCard"
                            className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                              uploadingFile === 'panCard'
                                ? 'border-cyan-500 bg-cyan-500/10'
                                : documentData.panCard 
                                  ? 'border-emerald-500 bg-emerald-500/10' 
                                  : documentErrors.panCard 
                                    ? 'border-red-500 bg-red-500/10' 
                                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                            }`}
                          >
                            {uploadingFile === 'panCard' ? (
                              <>
                                <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                                <span className="text-cyan-400 text-sm">Uploading...</span>
                              </>
                            ) : documentData.panCard ? (
                              <>
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                <span className="text-emerald-400 text-sm truncate max-w-[150px]">
                                  {documentData.panCard.filename || documentData.panCard.name || 'Uploaded'}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDocumentData(prev => ({ ...prev, panCard: null }));
                                  }}
                                  className="ml-2 text-slate-400 hover:text-red-400"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <Upload className="w-5 h-5 text-slate-400" />
                                <span className="text-slate-400 text-sm">Click to upload Pan Card (PDF, JPG, PNG)</span>
                              </>
                            )}
                          </label>
                        </div>
                        {documentErrors.panCard && <p className="text-red-400 text-xs">{documentErrors.panCard}</p>}
                      </div>
                    )}

                    {survey?.consent_form?.documentFields?.cancelledCheque && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Cancelled Cheque *</Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                            onChange={(e) => handleFileChange('cancelledCheque', e.target.files[0])}
                            className="hidden"
                            id="cancelledCheque"
                            disabled={uploadingFile === 'cancelledCheque'}
                          />
                          <label
                            htmlFor="cancelledCheque"
                            className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                              uploadingFile === 'cancelledCheque'
                                ? 'border-cyan-500 bg-cyan-500/10'
                                : documentData.cancelledCheque 
                                  ? 'border-emerald-500 bg-emerald-500/10' 
                                  : documentErrors.cancelledCheque 
                                    ? 'border-red-500 bg-red-500/10' 
                                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                            }`}
                          >
                            {uploadingFile === 'cancelledCheque' ? (
                              <>
                                <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                                <span className="text-cyan-400 text-sm">Uploading...</span>
                              </>
                            ) : documentData.cancelledCheque ? (
                              <>
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                <span className="text-emerald-400 text-sm truncate max-w-[150px]">
                                  {documentData.cancelledCheque.filename || documentData.cancelledCheque.name || 'Uploaded'}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDocumentData(prev => ({ ...prev, cancelledCheque: null }));
                                  }}
                                  className="ml-2 text-slate-400 hover:text-red-400"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <FileText className="w-5 h-5 text-slate-400" />
                                <span className="text-slate-400 text-sm">Click to upload Cancelled Cheque (PDF, JPG, PNG)</span>
                              </>
                            )}
                          </label>
                        </div>
                        {documentErrors.cancelledCheque && <p className="text-red-400 text-xs">{documentErrors.cancelledCheque}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Banking Details */}
              {(survey?.consent_form?.documentFields?.accountHolderName || 
                survey?.consent_form?.documentFields?.bankAccountNo ||
                survey?.consent_form?.documentFields?.ifscCode ||
                survey?.consent_form?.documentFields?.bankNameBranch) && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-400" /> Banking Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {survey?.consent_form?.documentFields?.accountHolderName && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Name of Account Holder *</Label>
                        <Input
                          value={documentData.accountHolderName}
                          onChange={(e) => setDocumentData(prev => ({ ...prev, accountHolderName: e.target.value }))}
                          placeholder="Enter full name as per bank account"
                          className={`bg-slate-800 border-slate-700 text-white ${documentErrors.accountHolderName ? 'border-red-500' : ''}`}
                        />
                        {documentErrors.accountHolderName && <p className="text-red-400 text-xs">{documentErrors.accountHolderName}</p>}
                      </div>
                    )}

                    {survey?.consent_form?.documentFields?.bankAccountNo && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Bank Account No. *</Label>
                        <Input
                          value={documentData.bankAccountNo}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setDocumentData(prev => ({ ...prev, bankAccountNo: value }));
                          }}
                          placeholder="Enter bank account number"
                          className={`bg-slate-800 border-slate-700 text-white ${documentErrors.bankAccountNo ? 'border-red-500' : ''}`}
                          inputMode="numeric"
                        />
                        {documentErrors.bankAccountNo && <p className="text-red-400 text-xs">{documentErrors.bankAccountNo}</p>}
                      </div>
                    )}

                    {survey?.consent_form?.documentFields?.ifscCode && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">IFSC Code *</Label>
                        <Input
                          value={documentData.ifscCode}
                          onChange={(e) => setDocumentData(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                          placeholder="e.g., SBIN0001234"
                          className={`bg-slate-800 border-slate-700 text-white uppercase ${documentErrors.ifscCode ? 'border-red-500' : ''}`}
                          maxLength={11}
                        />
                        {documentErrors.ifscCode && <p className="text-red-400 text-xs">{documentErrors.ifscCode}</p>}
                      </div>
                    )}

                    {survey?.consent_form?.documentFields?.bankNameBranch && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Bank Name & Branch *</Label>
                        <Input
                          value={documentData.bankNameBranch}
                          onChange={(e) => setDocumentData(prev => ({ ...prev, bankNameBranch: e.target.value }))}
                          placeholder="e.g., State Bank of India, MG Road Branch"
                          className={`bg-slate-800 border-slate-700 text-white ${documentErrors.bankNameBranch ? 'border-red-500' : ''}`}
                        />
                        {documentErrors.bankNameBranch && <p className="text-red-400 text-xs">{documentErrors.bankNameBranch}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-sm text-amber-300">
                  <strong>Note:</strong> Your documents and banking details will be stored securely and used only for processing honorarium payments.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setCurrentStep('consent')}
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={handleDocumentSubmit}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500"
                >
                  Continue to Survey <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Survey Questions Step */}
        {currentStep === 'survey' && (
          <>
            {/* Survey Header with Progress */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">{survey?.title}</h1>
              <p className="text-slate-400 text-sm sm:text-base mb-4">{survey?.description}</p>
              
              {/* Progress Bar */}
              <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-slate-400">Progress</span>
                  <span className="text-xs sm:text-sm font-medium text-white">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2 sm:h-3 bg-slate-700" />
                <div className="flex items-center justify-between mt-2 text-[10px] sm:text-xs text-slate-500">
                  <span>{survey?.questions?.filter(q => isQuestionAnswered(q)).length || 0} of {survey?.questions?.length || 0} answered</span>
                  <span>{survey?.questions?.filter(q => q.required && !isQuestionAnswered(q)).length || 0} required remaining</span>
                </div>
              </div>

              {/* Question Navigation Dots */}
              <div className="flex items-center justify-center gap-1.5 mt-4 flex-wrap">
                {survey?.questions?.map((q, i) => (
                  <button
                    key={q.id}
                    onClick={() => {
                      const el = document.getElementById(`question-${q.id}`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all ${
                      isQuestionAnswered(q)
                        ? 'bg-emerald-400'
                        : q.required
                          ? 'bg-amber-400/50'
                          : 'bg-slate-600'
                    }`}
                    aria-label={`Question ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Questions */}
            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {survey?.questions?.map((q, i) => renderQuestion(q, i))}
            </div>

            {survey?.questions?.length === 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-8 sm:p-12 text-center">
                  <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-400">This survey has no questions yet.</p>
                </CardContent>
              </Card>
            )}

            {/* Submit Button */}
            {survey?.questions?.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sticky bottom-4 sm:bottom-6 bg-slate-900/95 backdrop-blur-xl p-3 sm:p-4 rounded-xl border border-slate-800 safe-area-bottom">
                {survey?.consent_form && survey.consent_form.content && (
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('consent')}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800 order-2 sm:order-1"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                )}
                {(!survey?.consent_form || !survey.consent_form.content) && <div className="hidden sm:block" />}
                
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 order-1 sm:order-2">
                  {!canSubmit && (
                    <span className="text-amber-400 text-xs sm:text-sm text-center">
                      Answer all required questions to submit
                    </span>
                  )}
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !canSubmit}
                    className={`h-11 sm:h-12 px-6 sm:px-8 ${
                      canSubmit 
                        ? 'bg-gradient-to-r from-emerald-500 to-cyan-400 hover:from-emerald-600 hover:to-cyan-500' 
                        : 'bg-slate-700 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Submit Survey
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Thank You Step */}
        {currentStep === 'thankyou' && (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                {survey?.thank_you_message?.title || 'Thank You!'}
              </h2>
              <div className="text-slate-400 text-lg mb-8 max-w-md mx-auto whitespace-pre-wrap">
                {survey?.thank_you_message?.content || 'Your response has been recorded successfully. Thank you for participating in this survey.'}
              </div>
              <Button
                onClick={() => navigate('/doctor')}
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SurveyResponsePage;
